/**
 * Project settings: blur asset imagery for users who are not Admin or Trainer
 * unless asset JSON meta contains a configured key with a configured value.
 *
 * Selective bbox blur settings (labels to blur / keep visible) are applied
 * server-side on `/protected/asset/...`, crop APIs, and privacy-blur downloads.
 *
 * NOTE: Raster blurring is enforced server-side. These helpers remain for
 * settings UI and policy inspection; `shouldBlurAssetMeta` always returns
 * false so the UI does not double-blur.
 */

export const BLUR_UNLESS_META_ENABLED = 'blur_unless_meta_match_enabled';
export const BLUR_UNLESS_META_KEY = 'blur_unless_meta_key';
export const BLUR_UNLESS_META_VALUE = 'blur_unless_meta_value';

export const BBOX_BLUR_ENABLED = 'bbox_blur_enabled';
export const BBOX_BLUR_LABELS = 'bbox_blur_labels';
export const BBOX_BLUR_UNBLUR_LABELS = 'bbox_blur_unblur_labels';
/** Sentinel stored in bbox_blur_unblur_labels: keep every non-blur label visible. */
export const BBOX_BLUR_UNBLUR_ALL_OTHER = '__all_other_labels__';

function settingByName(settingsItems, name) {
  const items = Array.isArray(settingsItems) ? settingsItems : [];
  return items.find((s) => s && s.name === name);
}

/**
 * @param {Array<{name: string, value: string}>} settingsItems
 * @returns {{ enabled: boolean, key: string, expectedValue: string }}
 */
export function getBlurUnlessMetaFromSettings(settingsItems) {
  const en = settingByName(settingsItems, BLUR_UNLESS_META_ENABLED);
  const enabled =
    en &&
    (en.value === '1' ||
      en.value === 1 ||
      String(en.value).toLowerCase() === 'true');
  const keyRow = settingByName(settingsItems, BLUR_UNLESS_META_KEY);
  const valRow = settingByName(settingsItems, BLUR_UNLESS_META_VALUE);
  const key = keyRow && keyRow.value != null ? String(keyRow.value).trim() : '';
  const expectedValue =
    valRow && valRow.value != null && String(valRow.value).trim() !== ''
      ? String(valRow.value).trim()
      : 'true';
  return {
    enabled: Boolean(enabled && key.length > 0),
    key,
    expectedValue,
  };
}

/**
 * @param {Array<{name: string, value: string}>} settingsItems
 * @returns {{ enabled: boolean, blurLabels: string[], unblurLabels: string[] }}
 */
export function getBBoxBlurFromSettings(settingsItems) {
  const en = settingByName(settingsItems, BBOX_BLUR_ENABLED);
  const enabled =
    en &&
    (en.value === '1' ||
      en.value === 1 ||
      String(en.value).toLowerCase() === 'true');
  const blurRow = settingByName(settingsItems, BBOX_BLUR_LABELS);
  const unblurRow = settingByName(settingsItems, BBOX_BLUR_UNBLUR_LABELS);
  return {
    enabled: Boolean(enabled),
    blurLabels: parseLabelListSetting(blurRow && blurRow.value),
    unblurLabels: parseLabelListSetting(unblurRow && unblurRow.value),
  };
}

export function parseLabelListSetting(raw) {
  if (raw == null || String(raw).trim() === '') {
    return [];
  }
  const s = String(raw).trim();
  try {
    const parsed = JSON.parse(s);
    if (Array.isArray(parsed)) {
      return parsed
        .map((x) => String(x).trim())
        .filter(Boolean);
    }
  } catch (_) { /* fall through */ }
  return s.split(',').map((x) => x.trim()).filter(Boolean);
}

function normalizeMetaValue(v) {
  if (v === true) return 'true';
  if (v === false) return 'false';
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v).trim().toLowerCase();
}

export function metaValueMatchesExpected(metaVal, expectedStr) {
  const exp = String(expectedStr).trim();
  const mv = normalizeMetaValue(metaVal);
  const ex = normalizeMetaValue(exp);
  return mv === ex;
}

/**
 * @param {string|object|undefined|null} metaStrOrObj — asset.meta string or parsed object
 * @param {{ enabled: boolean, key: string, expectedValue: string }} policy
 * @param {boolean} isExempt — Admin or Trainer on this project
 */
export function shouldBlurAssetMeta(metaStrOrObj, policy, isExempt) {
  void metaStrOrObj;
  void policy;
  void isExempt;
  return false;
}
